import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components/macro';
import Page from '@nomady/shared/routeConfiguration/Page';
import env from '@nomady/shared/utils/env';
import { NamedLink } from '../../components';
import { ColorVariant } from '../../theme';
import Button, { ButtonColorVariant, ButtonSizeVariant, } from '../Button/Button';
import Text, { TextVariant } from '../Text/Text';
import Logo, { LogoVariant } from '../Logo/Logo';
import AppStoreButton, { StoreType } from '../AppStoreButton/AppStoreButton';
import MediaIcon, { MediaType } from '../MediaIcon/MediaIcon';
import PaymentIcon, { PaymentType } from '../PaymentIcon/PaymentIcon';
import NomadyStamp from '../NomadyStamp/NomadyStamp';
import { isApp } from '../../util/deviceContext';
export var FooterVariant;
(function (FooterVariant) {
    FooterVariant["Full"] = "Full";
    FooterVariant["Light"] = "Light";
})(FooterVariant || (FooterVariant = {}));
const FooterMainWrapper = styled.section ``;
const Wrapper = styled.div `
  ${props => props.theme.layout.gridContainer};
  background-color: ${props => props.theme.color.basicRavenBlack};

  padding-top: 64px;
  padding-bottom: 72px;

  @media ${props => props.theme.device.tablet} {
    padding-bottom: 56px;
  }
`;
const Content = styled.div `
  position: relative;
  grid-column-start: contentStart;
  grid-column-end: contentEnd;

  display: flex;
  flex-direction: column;
  gap: 32px;

  @media ${props => props.theme.device.tablet} {
    gap: 64px;
  }
`;
const LinkWrapper = styled.div `
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  column-gap: 20px;
  row-gap: 32px;

  @media ${props => props.theme.device.tablet} {
    grid-template-columns: repeat(12, 1fr);
  }
`;
const LinkColumn = styled.div `
  grid-column: span 3;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const LegalLinkWrapper = styled.div `
  ${props => props.theme.layout.gridContainer};
`;
const LegalLinkContent = styled.div `
  grid-column-start: contentStart;
  grid-column-end: contentEnd;
  padding-top: 32px;
  padding-bottom: 40px;

  @media ${props => props.theme.device.tabletL} {
    padding-right: 180px;
  }
`;
const StyledPrivacySettingsLink = styled.button `
  border: 0;
  padding: 0;
  cursor: pointer;
`;
const TopRow = styled.div `
  display: flex;
  flex-direction: row;
  column-gap: 32px;
  flex-wrap: wrap;
  align-items: center;
  justify-content: start;

  > * {
    height: 56px;

    @media ${props => props.theme.device.desktopL} {
      grid-column: span 3;
    }
  }

  @media ${props => props.theme.device.desktopL} {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
  }
`;
const LogoWrapper = styled.div `
  display: flex;
  align-items: center;
  justify-content: start;
`;
const SocialMedia = styled.div `
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: center;
`;
const Cta = styled.div `
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;
const AdvertBanner = styled.div `
  display: flex;
  flex-direction: column;
  gap: 20px;
  justify-content: space-between;

  @media ${props => props.theme.device.tabletL} {
    flex-direction: row;
  }

  @media ${props => props.theme.device.desktopL} {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
  }
`;
const AppAd = styled.div `
  display: flex;
  flex-direction: column;
  gap: 16px;
  @media ${props => props.theme.device.desktopL} {
    grid-column: span 6;
  }
`;
const StoreButtonsWrapper = styled.div `
  display: flex;
  gap: 16px;
`;
const MissionStatement = styled.div `
  @media ${props => props.theme.device.desktopL} {
    grid-column: span 6;
  }
`;
const BottomContentWrapper = styled.div `
  display: flex;
  flex-direction: column;
  gap: 32px;
`;
const PaymentMethodsWrapper = styled.div `
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
const PaymentMethods = styled.div `
  display: flex;
  flex-direction: row;
  gap: 8px;
  flex-wrap: wrap;
`;
const NomadyStampWrapper = styled.div `
  position: absolute;
  bottom: -72px;
  right: 0;
  transform: rotate(20deg) translateY(30%);
  width: 64px;

  @media ${props => props.theme.device.tablet} {
    bottom: -56px;
  }

  @media ${props => props.theme.device.tabletL} {
    width: 180px;
  }
`;
const Footer = () => {
    const intl = useIntl();
    return (_jsxs(FooterMainWrapper, { children: [_jsx(Wrapper, { children: _jsxs(Content, { children: [_jsxs(AdvertBanner, { children: [!isApp && (_jsxs(AppAd, { children: [_jsx(Text, Object.assign({ variant: TextVariant.H5SB, color: ColorVariant.FunctionStoneGreyInactive }, { children: intl.formatMessage({ id: 'Footer.appAd' }) })), _jsxs(StoreButtonsWrapper, { children: [_jsx(AppStoreButton, { store: StoreType.GooglePlay }), _jsx(AppStoreButton, { store: StoreType.AppStore })] })] })), _jsx(MissionStatement, { children: _jsx(Text, Object.assign({ variant: TextVariant.P1M, color: ColorVariant.FunctionStoneGreyInactive }, { children: intl.formatMessage({ id: 'Footer.missionStatement' }) })) })] }), _jsxs(BottomContentWrapper, { children: [_jsxs(TopRow, { children: [_jsx(LogoWrapper, { children: _jsx(NamedLink, Object.assign({ name: Page.Landing }, { children: _jsx(Logo, { colorVariant: ColorVariant.BasicPearlWhiteTypeWhite, variant: LogoVariant.Default }) })) }), _jsxs(SocialMedia, { children: [_jsx(MediaIcon, { media: MediaType.Facebook }), _jsx(MediaIcon, { media: MediaType.Instagram }), _jsx(MediaIcon, { media: MediaType.LinkedIn }), _jsx(MediaIcon, { media: MediaType.YouTube }), _jsx(MediaIcon, { media: MediaType.WhatsApp }), _jsx(MediaIcon, { media: MediaType.Mail })] }), _jsx(Cta, { children: _jsx(Button, { label: intl.formatMessage({ id: 'Footer.ctaButton' }), targetRoute: Page.NewListing, sizeVariant: ButtonSizeVariant.Small, colorVariant: ButtonColorVariant.Default }) })] }), _jsxs(LinkWrapper, { children: [_jsxs(LinkColumn, { children: [_jsx(Text, Object.assign({ variant: TextVariant.H5SB, colorVariant: ColorVariant.FunctionStoneGreyInactive }, { children: _jsx(FormattedMessage, { id: "Footer.experience" }) })), _jsx(NamedLink, Object.assign({ name: Page.Search }, { children: _jsx(Text, Object.assign({ variant: TextVariant.P3R, colorVariant: ColorVariant.FunctionStoneGreyInactive }, { children: _jsx(FormattedMessage, { id: "Footer.discover" }) })) })), _jsx(NamedLink, Object.assign({ name: Page.BuyGiftCard }, { children: _jsx(Text, Object.assign({ variant: TextVariant.P3R, colorVariant: ColorVariant.FunctionStoneGreyInactive }, { children: _jsx(FormattedMessage, { id: "Footer.giftcards" }) })) })), _jsx(NamedLink, Object.assign({ name: Page.Inspirations }, { children: _jsx(Text, Object.assign({ variant: TextVariant.P3R, colorVariant: ColorVariant.FunctionStoneGreyInactive }, { children: _jsx(FormattedMessage, { id: "Footer.inspiration" }) })) })), _jsx(NamedLink, Object.assign({ name: Page.Regions }, { children: _jsx(Text, Object.assign({ variant: TextVariant.P3R, colorVariant: ColorVariant.FunctionStoneGreyInactive }, { children: _jsx(FormattedMessage, { id: "Footer.region" }) })) }))] }), _jsxs(LinkColumn, { children: [_jsx(Text, Object.assign({ variant: TextVariant.H5SB, colorVariant: ColorVariant.FunctionStoneGreyInactive }, { children: _jsx(FormattedMessage, { id: "Footer.faq" }) })), _jsx(NamedLink, Object.assign({ name: Page.Host }, { children: _jsx(Text, Object.assign({ variant: TextVariant.P3R, colorVariant: ColorVariant.FunctionStoneGreyInactive }, { children: _jsx(FormattedMessage, { id: "Footer.host" }) })) })), _jsx(NamedLink, Object.assign({ name: Page.Camper }, { children: _jsx(Text, Object.assign({ variant: TextVariant.P3R, colorVariant: ColorVariant.FunctionStoneGreyInactive }, { children: _jsx(FormattedMessage, { id: "Footer.camper" }) })) })), _jsx(NamedLink, Object.assign({ name: Page.Scout }, { children: _jsx(Text, Object.assign({ variant: TextVariant.P3R, colorVariant: ColorVariant.FunctionStoneGreyInactive }, { children: _jsx(FormattedMessage, { id: "Footer.scout" }) })) }))] }), _jsxs(LinkColumn, { children: [_jsx(Text, Object.assign({ variant: TextVariant.H5SB, colorVariant: ColorVariant.FunctionStoneGreyInactive }, { children: _jsx(FormattedMessage, { id: "Footer.about" }) })), _jsx(NamedLink, Object.assign({ name: Page.Vision }, { children: _jsx(Text, Object.assign({ variant: TextVariant.P3R, colorVariant: ColorVariant.FunctionStoneGreyInactive }, { children: _jsx(FormattedMessage, { id: "Footer.vision" }) })) })), _jsx(NamedLink, Object.assign({ name: Page.NomadyPactMain }, { children: _jsx(Text, Object.assign({ variant: TextVariant.P3R, colorVariant: ColorVariant.FunctionStoneGreyInactive }, { children: _jsx(FormattedMessage, { id: "Footer.theNomadyPact" }) })) })), _jsx(NamedLink, Object.assign({ name: Page.About }, { children: _jsx(Text, Object.assign({ variant: TextVariant.P3R, colorVariant: ColorVariant.FunctionStoneGreyInactive }, { children: _jsx(FormattedMessage, { id: "Footer.aboutUs" }) })) })), _jsx(NamedLink, Object.assign({ name: Page.Jobs }, { children: _jsx(Text, Object.assign({ variant: TextVariant.P3R, colorVariant: ColorVariant.FunctionStoneGreyInactive }, { children: _jsx(FormattedMessage, { id: "Footer.jobs" }) })) })), _jsx(NamedLink, Object.assign({ name: Page.Fusion }, { children: _jsx(Text, Object.assign({ variant: TextVariant.P3R, colorVariant: ColorVariant.FunctionStoneGreyInactive }, { children: _jsx(FormattedMessage, { id: "Footer.fusion" }) })) }))] }), _jsxs(LinkColumn, { children: [_jsx(Text, Object.assign({ variant: TextVariant.H5SB, colorVariant: ColorVariant.FunctionStoneGreyInactive }, { children: _jsx(FormattedMessage, { id: "Footer.getInTouch" }) })), _jsx(NamedLink, Object.assign({ name: Page.Contact }, { children: _jsx(Text, Object.assign({ variant: TextVariant.P3R, colorVariant: ColorVariant.FunctionStoneGreyInactive }, { children: _jsx(FormattedMessage, { id: "Footer.contact" }) })) })), _jsx(NamedLink, Object.assign({ name: Page.Partner }, { children: _jsx(Text, Object.assign({ variant: TextVariant.P3R, colorVariant: ColorVariant.FunctionStoneGreyInactive }, { children: _jsx(FormattedMessage, { id: "Footer.partner" }) })) })), _jsx(NamedLink, Object.assign({ name: Page.Media }, { children: _jsx(Text, Object.assign({ variant: TextVariant.P3R, colorVariant: ColorVariant.FunctionStoneGreyInactive }, { children: _jsx(FormattedMessage, { id: "Footer.media" }) })) }))] })] }), _jsxs(PaymentMethodsWrapper, { children: [_jsx(Text, Object.assign({ variant: TextVariant.H5SB, colorVariant: ColorVariant.FunctionStoneGreyInactive }, { children: _jsx(FormattedMessage, { id: "Footer.payWith" }) })), _jsxs(PaymentMethods, { children: [_jsx(PaymentIcon, { paymentType: PaymentType.Mastercard }), _jsx(PaymentIcon, { paymentType: PaymentType.PayPal }), _jsx(PaymentIcon, { paymentType: PaymentType.Visa }), _jsx(PaymentIcon, { paymentType: PaymentType.Klarna }), _jsx(PaymentIcon, { paymentType: PaymentType.GooglePay }), _jsx(PaymentIcon, { paymentType: PaymentType.ApplePay }), _jsx(PaymentIcon, { paymentType: PaymentType.GiroPay }), _jsx(PaymentIcon, { paymentType: PaymentType.Twint })] })] })] }), _jsx(NomadyStampWrapper, { children: _jsx(NomadyStamp, { colorVariant: ColorVariant.FunctionStoneGreyInactive }) })] }) }), _jsx(LegalLinkWrapper, { children: _jsx(LegalLinkContent, { children: _jsxs(Text, Object.assign({ variant: TextVariant.L4R, colorVariant: ColorVariant.FunctionStoneGreyInactive }, { children: [_jsx(NamedLink, Object.assign({ name: "TermsOfServicePage" }, { children: _jsx(FormattedMessage, { id: "Footer.termsOfUse" }) })), `\u00A0\u00A0\u0020|\u00A0\u00A0\u00A0`, _jsx(NamedLink, Object.assign({ name: "GiftCardTermsPage" }, { children: _jsx(FormattedMessage, { id: "Footer.giftCardTerms" }) })), `\u00A0\u00A0\u0020|\u00A0\u00A0\u00A0`, _jsx(NamedLink, Object.assign({ name: "ConditionsOfParticipationPage" }, { children: _jsx(FormattedMessage, { id: "Footer.conditionsOfParticipation" }) })), `\u00A0\u00A0\u0020|\u00A0\u00A0\u00A0`, _jsx(NamedLink, Object.assign({ name: "LegalNoticePage" }, { children: _jsx(FormattedMessage, { id: "Footer.legalNotice" }) })), `\u00A0\u00A0\u0020|\u00A0\u00A0\u00A0`, _jsx(NamedLink, Object.assign({ name: "PrivacyPolicyPage" }, { children: _jsx(FormattedMessage, { id: "Footer.privacyPolicy" }) })), env('USERCENTRICS_SETTINGS_ID') && (_jsxs(_Fragment, { children: [`\u00A0\u00A0\u0020|\u00A0\u00A0\u00A0`, _jsx(StyledPrivacySettingsLink, Object.assign({ onClick: () => { var _a; return (_a = window.UC_UI) === null || _a === void 0 ? void 0 : _a.showSecondLayer(); } }, { children: _jsx(FormattedMessage, { id: "Footer.privacySettings" }) }))] })), `\u00A0\u00A0\u0020|\u00A0\u00A0\u00A0`, _jsx(NamedLink, Object.assign({ name: "NomadyPactPage" }, { children: _jsx(FormattedMessage, { id: "Footer.nomadyPact" }) })), `\u00A0\u00A0\u0020|\u00A0\u00A0\u00A0`, _jsx(NamedLink, Object.assign({ name: "FeesPage" }, { children: _jsx(FormattedMessage, { id: "Footer.fees" }) }))] })) }) })] }));
};
export default Footer;
